import type { Activity, ItineraryDay, JournalEntry, TripPhoto } from '../types';

export type JournalView = 'list' | 'map';

export interface PhotoPlace {
  stop: Activity;
  day: ItineraryDay;
  lat: number;
  lng: number;
  photos: TripPhoto[];
}

/**
 * The photos on the map, one point per stop (ADR-0042: a photo's location is its
 * stop's). Photos without a stop, or whose stop has no coordinates, are counted
 * in `unplaced` — never guessed.
 */
export function photoPlaces(photos: TripPhoto[], days: ItineraryDay[]): { places: PhotoPlace[]; unplaced: number } {
  const stops = new Map<string, { stop: Activity; day: ItineraryDay }>();
  for (const day of days) for (const stop of day.activities) stops.set(stop.id, { stop, day });

  const byStop = new Map<string, PhotoPlace>();
  let unplaced = 0;
  for (const photo of photos) {
    const found = photo.activityId ? stops.get(photo.activityId) : undefined;
    // Prisma's Decimal reaches the client as a string ("64.1466"): a map given that crashes.
    const lat = Number(found?.stop.latitude ?? NaN);
    const lng = Number(found?.stop.longitude ?? NaN);
    if (!found || found.stop.latitude == null || found.stop.longitude == null || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      unplaced++;
      continue;
    }
    const place = byStop.get(found.stop.id);
    if (place) place.photos.push(photo);
    else byStop.set(found.stop.id, { ...found, lat, lng, photos: [photo] });
  }
  return { places: [...byStop.values()], unplaced };
}

/**
 * Every photo of the all-days view, in the order it is drawn: per day its loose
 * photos and then each entry's, and the photos without a day last. The viewer
 * steps through this so the last photo of a day leads to the next day.
 */
export function overviewPhotoSequence(
  days: ItineraryDay[],
  entries: JournalEntry[],
  photos: TripPhoto[],
): TripPhoto[] {
  const byId = new Map(photos.map(p => [p.id, p]));
  const sequence: TripPhoto[] = [];
  for (const day of days) {
    sequence.push(...photos.filter(p => p.dayId === day.id && !p.journalEntryId));
    for (const entry of entries) {
      if (entry.dayId !== day.id) continue;
      for (const id of entry.photoIds) {
        const photo = byId.get(id);
        if (photo) sequence.push(photo);
      }
    }
  }
  sequence.push(...photos.filter(p => p.dayId === null));
  return sequence;
}
